import React from "react";
import SkeletonLoader from "../../Components/SkeletonLoader/SkeletonLoader";
import "../order-summary/orderSummaryShell.scss";
import "./review.tokens.scss";
import "./SummaryPriceCard.scss";

/**
 * Presentational price + policy + CTA region. No API, no currency conversion.
 *
 * @param {string} [title]
 * @param {string} [statusLabel]
 * @param {string} [totalLabel]
 * @param {React.ReactNode} [detailsSlot] optional block between header and price (e.g. package meta rows)
 * @param {boolean} [priceLoading] when true, show skeleton instead of empty price dash
 * @param {string|null} [primaryAmountDisplay] main price line (e.g. "3,458 ₪")
 * @param {string|null} [secondaryAmountDisplay] e.g. "(1,034$)"
 * @param {{ key: string, label: string, valueText: string }[]} [breakdownRows]
 * @param {React.ReactNode} [policySlot] cancellation / terms area
 * @param {React.ReactNode} [footer] CTA cluster (use BookingCTA)
 * @param {"default"|"compact"} [density]
 * @param {boolean} [showPriceBlock] — when false, hide price label/row (meta-only cards)
 * @param {boolean} [orderSummaryRailChrome] — vacation-package-style rail card (sidebar order summary only)
 */
export default function SummaryPriceCard({
  title = "סיכום הזמנה",
  statusLabel,
  totalLabel = 'סה״כ לתשלום',
  detailsSlot,
  priceLoading = false,
  primaryAmountDisplay,
  secondaryAmountDisplay,
  breakdownRows = [],
  policySlot,
  footer,
  density = "default",
  showPriceBlock = true,
  orderSummaryRailChrome = false,
}) {
  const d = density === "compact" ? "compact" : "default";
  const railClass =
    d === "compact" || !orderSummaryRailChrome ? "" : " os-price-rail";

  return (
    <div
      className={`review-ds review-summary-card review-summary-card--${d}${railClass}`.trim()}
      data-review-summary-card
    >
      {(title || statusLabel) && (
        <header className="review-summary-card__header">
          {title ? (
            <h2
              className={[
                "review-summary-card__title",
                railClass ? "os-shell__title" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {title}
            </h2>
          ) : null}
          {statusLabel ? (
            <span className="review-summary-card__badge" role="status">
              {statusLabel}
            </span>
          ) : null}
        </header>
      )}

      {detailsSlot ? (
        <div className="review-summary-card__details">{detailsSlot}</div>
      ) : null}

      {showPriceBlock || breakdownRows.length > 0 ? (
        <div className="review-summary-card__price-stack">
          {showPriceBlock ? (
            <div className="review-summary-card__price-block">
              {totalLabel ? (
                <p className="review-summary-card__price-label">{totalLabel}</p>
              ) : null}
              <div className="review-summary-card__price-row" translate="no">
                {priceLoading ? (
                  <SkeletonLoader style={{ width: 120, height: 22 }} />
                ) : primaryAmountDisplay ? (
                  <span className="review-summary-card__price-primary">{primaryAmountDisplay}</span>
                ) : (
                  <span className="review-summary-card__price-primary review-summary-card__price-primary--empty">
                    —
                  </span>
                )}
                {!priceLoading && secondaryAmountDisplay ? (
                  <span className="review-summary-card__price-secondary">{secondaryAmountDisplay}</span>
                ) : null}
              </div>
            </div>
          ) : null}
          {breakdownRows.length > 0 ? (
            <ul className="review-summary-card__breakdown">
              {breakdownRows.map((row) => (
                <li key={row.key} className="review-summary-card__breakdown-row">
                  <span>{row.label}</span>
                  <span className="review-summary-card__breakdown-value" translate="no">
                    {row.valueText}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      {policySlot ? <div className="review-summary-card__policy">{policySlot}</div> : null}

      {footer ? <div className="review-summary-card__footer">{footer}</div> : null}
    </div>
  );
}
